import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useSwitchCompany from 'hooks/useSwitchCompany';
import { useUserInfo } from 'pages/UserProvider';
import { getDefaultLandingPage } from 'routes/routes';
import { spoofRoles } from '@/utils/userRoles';

export const companyIdParam = 'companyId';

/**
 * Applies a company context requested via the ?companyId= query param —
 * used by customer-success Slack deep links (ENG-573). A Slack link opens a
 * fresh tab whose sessionStorage carries no spoof state, so a member-profile
 * link for another company would 404; this hook spoofs into the requested
 * company first, then strips the param.
 *
 * Returns true while the switch is pending so the caller can hold rendering —
 * otherwise the target page's queries run against the wrong company and its
 * 404 redirect fires before the spoof completes.
 *
 * Non-internal roles can't spoof (enforced server-side too), so for them the
 * param is stripped without a company switch.
 *
 * If the spoof fails (company lookup rejects, or the switch never completes),
 * the user is sent to the default landing page — the deep-linked page would
 * only 404 against the wrong company.
 */
const useSpoofFromUrl = (): boolean => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, company } = useUserInfo();
  const requestedCompanyId = searchParams.get(companyIdParam);
  const currentCompanyId = company?.id;
  const isPending = requestedCompanyId !== null && requestedCompanyId !== currentCompanyId;

  const clearParam = useCallback((): void => {
    setSearchParams(
      (params) => {
        params.delete(companyIdParam);
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const failSpoof = useCallback((): void => {
    navigate(getDefaultLandingPage(), { replace: true });
  }, [navigate]);

  const { switchTo } = useSwitchCompany(failSpoof);

  useEffect(() => {
    if (!requestedCompanyId || !user) {
      return;
    }

    // Already in the requested company (also reached after a successful
    // switch, which re-runs this effect with the updated company).
    if (requestedCompanyId === currentCompanyId) {
      clearParam();
      return;
    }

    if (!spoofRoles.includes(user.roleName ?? '')) {
      clearParam();
      return;
    }

    switchTo(requestedCompanyId);
  }, [requestedCompanyId, currentCompanyId, user, clearParam, switchTo]);

  return isPending;
};

export default useSpoofFromUrl;
